import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { ChevronLeft, ShoppingCart } from "react-feather";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import Swal from "sweetalert2";
import { Isku, useProduct } from "../../client/product/useProduct";
import Button from "../../components/Button";
import Header from "../../components/Header";
import Spinner from "../../components/Spinner";
import { Caption, Heading4 } from "../../components/Typography";
import { COLOR_WHITE, COLOR_YELLOW } from "../../constants/colors";
import { useAppContext } from "../../contexts/App";
import bgGray from "../../images/bg-gray.png";
import { CartItem } from "../../types/Product";
import InformationSection from "./InformationSection";
import ProductNotFound from "./ProductNotFound";
import QtyInput from "./QtyInput";
import OptionPicker from "./OptionPicker";

const Background = styled.div`
  min-height: 100vh;
  background-image: url(${bgGray});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;

const StyledContainer = styled(Container)`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 50vh;
`;

const IcBack = styled(ChevronLeft)`
  width: 18px;
  height: 18px;

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
  }
`;

const ButtonCart = styled(Button)`
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const IcCart = styled(ShoppingCart)`
  width: 28px;
  height: 28px;

  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
  }
`;

const ImgWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  padding-top: 100%;
  position: relative;
  background-color: ${COLOR_WHITE};
  overflow: hidden;
  border-radius: 8px;

  @media (max-width: 768px) {
    width: 90%;
  }
`;

const ImgProduct = styled.img`
  position: absolute;
  z-index: 2;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const BackText = styled.a`
  ${Caption}

  width: fit-content;
  display: flex;
  align-items: center;
  color: ${COLOR_WHITE};

  position: absolute;
  top: -28px;
  left: 8px;

  &:hover {
    color: ${COLOR_WHITE};
  }
`;

const Title = styled.h1`
  ${Heading4}
  color: ${COLOR_YELLOW};
`;

const Price = styled.div`
  ${Heading4}
  color: ${COLOR_WHITE};
`;

const OtherImagesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const OtherImage = styled.img`
  width: 100px;
  height: 100px;
  cursor: pointer;
  object-fit: contain;
  background-color: white;
  border-radius: 8px;
`;

function Product() {
  const { id } = useParams();
  const appContext = useAppContext();
  const { isLoading, isFetching, data: product } = useProduct(id || "");

  const [sku, setSku] = useState<Isku | undefined>();
  const [qty, setQty] = useState(1);
  const [productQtyInCart, setProductQtyInCart] = useState(0);
  const [informations, setInformations] = useState<{ label: string; value: string }[]>([]);

  const [selectedImage, setSelectedImage] = useState<string>();

  const imageWrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (product) {
      setSelectedImage(product.images[0]);
    }
  }, [product]);

  const firstAvailableSize = useMemo(() => {
    const skuTemp = product?.skus.find((item) => item.quantity > 0);
    if (skuTemp && skuTemp.quantity > productQtyInCart) {
      return skuTemp;
    }
    return undefined;
  }, [product, productQtyInCart]);

  useEffect(() => {
    setSku(firstAvailableSize);
  }, [firstAvailableSize]);

  useEffect(() => {
    const storedProducts = localStorage.getItem("cart");
    const parsedProducts: CartItem[] = JSON.parse(storedProducts || "[]");
    const productInCart = parsedProducts.find((item) => item.id === product?._id && item.option === sku?.option);
    if (productInCart) {
      setProductQtyInCart(productInCart.count);
    }
  }, [product, sku]);

  useEffect(() => {
    if (!product?.description) return;
    setInformations([
      {
        label: "Description",
        value: product.description,
      },
      {
        label: "Specification",
        value: product.skus.find((o) => o.option === sku?.option)?.specification || "No specifications defined",
      },
    ]);
  }, [product, sku]);

  const handleAddToCart = () => {
    const storedProducts = localStorage.getItem("cart");
    let parsedProducts: CartItem[] = JSON.parse(storedProducts || "[]");

    const newProduct: CartItem = {
      id: product!._id,
      count: qty,
      option: sku?.option || "",
      storedAt: Date.now(),
      shipping: product!.shipping || 0
    };

    const previousProduct = parsedProducts.find(
      (item) => item.id === newProduct.id && item.option === newProduct.option
    );
    const totalBoughtQty = qty + productQtyInCart;

    let newProducts: CartItem[] = [];
    if (previousProduct) {
      parsedProducts = parsedProducts.filter(
        (item) => !(item.id === newProduct.id && item.option === newProduct.option)
      );
      newProduct.count = totalBoughtQty;
    }
    newProducts = [...parsedProducts, newProduct];

    const productQty = sku?.quantity || 0;
    if (newProduct.count > productQty) {
      Swal.fire({
        icon: "error",
        title: "Product out of stock",
        text: `You can't add more than ${productQty - productQtyInCart} items`,
        showConfirmButton: false,
        timer: 1300,
        timerProgressBar: true,
      });
      return;
    }

    setProductQtyInCart(newProduct.count);

    Swal.fire({
      icon: "success",
      title: "Success",
      text: "Product added to cart",
      showConfirmButton: false,
      timer: 1300,
      timerProgressBar: true,
    });

    appContext?.setCartItemCount(newProducts.length);
    localStorage.setItem("cart", JSON.stringify(newProducts));
  };

  return (
    <Background>
      <Header />
      <StyledContainer>
        {(isLoading || isFetching) && <Spinner animation="border" />}
        {!isLoading && !isFetching && product && (
          <Row>
            <Col md={4} className="mb-3 d-flex flex-column">
              <BackText href="/" className="mb-1 mb-sm-2 d-none d-md-flex">
                <IcBack />
                Back to home
              </BackText>
              <ImgWrapper className="mb-3" ref={imageWrapper}>
                <ImgProduct src={selectedImage} alt={product?.name} className="w-100" />
              </ImgWrapper>
              <OtherImagesContainer>
                {product?.images.map((image) => (
                  <OtherImage
                    src={image}
                    alt={product?.name}
                    onClick={() => {
                      setSelectedImage(image);
                      imageWrapper.current?.scrollIntoView();
                    }}
                  />
                ))}
              </OtherImagesContainer>
            </Col>
            <Col md={8} className="mb-3 pb-5">
              <Title>{product?.name}</Title>
              <Price className="mb-4">${product?.price.toFixed(2)}</Price>
              {informations.length > 0 && <InformationSection informations={informations} />}
              <br />
              <OptionPicker skus={product.skus} selectedOption={sku} setSelectedOption={setSku} />
              <br />
              <QtyInput qty={qty} setQty={setQty} isAvailable={!!firstAvailableSize} />
              <br />
              <ButtonCart variant="primary" className="px-4" onClick={handleAddToCart} disabled={!firstAvailableSize}>
                <IcCart className="me-2" />
                Add to Cart
              </ButtonCart>
            </Col>
          </Row>
        )}
        {!isLoading && !isFetching && !product && <ProductNotFound />}
      </StyledContainer>
    </Background>
  );
}

export default Product;
